/**
 * AppShell — layout route for every non-marketing route (auth, protected
 * app, admin).
 *
 * This is a *pathless* react-router layout route: it owns no URL segment
 * of its own, it only wraps its children with:
 *
 * - `ConfigProvider` — runtime configuration (fetched client-side).
 * - `AuthProvider` — Supabase session/user state.
 * - the top-level "Initializing…" / "Configuration Error" loading states
 *   that previously lived in `App.tsx`.
 *
 * Marketing/public routes (see `src/app/marketingRoutes.ts`) are
 * deliberately **not** nested under `AppShell` — they render standalone so
 * that `vite-react-ssg` can pre-render them to static HTML without ever
 * mounting the config/auth providers (which depend on `useEffect` and a
 * runtime `fetch`, neither of which run during static generation). This
 * keeps SSG output limited to plain, provider-free marketing markup and
 * keeps auth/config concerns entirely out of the statically generated
 * pages, per GH issue #267 acceptance criteria ("static generation must
 * not interfere with authenticated SPA routes or dynamic data fetching").
 *
 * Also mounts the unified API client's `QueryClientProvider`
 * (`@/lib/api/apiClient`) and a single `sonner` `<Toaster />` (GitHub
 * issue "Unified API client with RTK Query for all frontend data
 * fetching", Requirement #8) so every non-marketing route's TanStack
 * Query hooks share one cache and every `toast.error(...)` call site has
 * somewhere to render — mirrors `ConfigProvider`/`AuthProvider` being
 * mounted once here rather than per-route.
 */
import { Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { ConfigProvider, AuthProvider, useAuth, useConfigOptional } from './providers';
import { queryClient } from '@/lib/api/apiClient';

function AppShellContent() {
  const auth = useAuth();
  const config = useConfigOptional();

  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="text-lg font-semibold text-foreground">Initializing...</div>
          <p className="text-sm text-muted-foreground mt-2">
            Please wait while we load your session
          </p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Configuration Error</div>
          <p className="text-sm text-muted-foreground mt-2">
            Failed to load application configuration. Please refresh the page.
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
}

export default function AppShell() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <AuthProvider>
          <AppShellContent />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
